import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import SaveIcon from "@mui/icons-material/Save";
import { fetchInlineEdit } from "./inlineEditApi";
import {
  applyInlineEditResult,
  getSectionContent,
  getSectionOptions,
} from "./resumeDataUtils";

export default function AdvancedInlineEditor({
  optimizedData,
  jobDescription,
  jobTitle,
  onApplyUpdate,
  onBack,
  onRegenerate,
  onSave,
  saving = false,
  regenerating,
  selectedSection: controlledSection,
  onSectionChange,
}) {
  const sectionOptions = useMemo(
    () => getSectionOptions(optimizedData),
    [optimizedData]
  );

  const [internalSection, setInternalSection] = useState(
    sectionOptions[0]?.key || "summary"
  );
  const [userPrompt, setUserPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const promptRef = useRef(null);

  const selectedSection = controlledSection ?? internalSection;
  const setSelectedSection = onSectionChange ?? setInternalSection;

  useEffect(() => {
    if (!sectionOptions.some((option) => option.key === selectedSection)) {
      setSelectedSection(sectionOptions[0]?.key || "summary");
    }
  }, [sectionOptions, selectedSection, setSelectedSection]);

  useEffect(() => {
    promptRef.current?.focus();
  }, [selectedSection]);

  const activeSection = sectionOptions.find((item) => item.key === selectedSection);

  const handleApplyEdit = async () => {
    if (!activeSection) return;

    const currentContent = getSectionContent(optimizedData, selectedSection);
    if (currentContent === null || currentContent === undefined) {
      setError("Selected section has no content to edit.");
      return;
    }

    if (!userPrompt.trim()) {
      setError("Please enter a prompt describing the change you want.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // When education/certificate is empty, add a schema hint so the AI returns
      // properly structured objects instead of flat strings.
      let enrichedPrompt = userPrompt.trim();
      if (Array.isArray(currentContent) && currentContent.length === 0) {
        if (selectedSection === "education") {
          enrichedPrompt += `\n\nIMPORTANT: Return each education entry as an object with keys: "university", "yearofpassing", "degree". Example: {"university": "MIT", "yearofpassing": "2020", "degree": "Bachelor of Science"}`;
        } else if (selectedSection === "certificate") {
          enrichedPrompt += `\n\nIMPORTANT: Return each certificate entry as an object with key: "certName" only. Example: {"certName": "AWS Solutions Architect"}`;
        }
      }

      const updatedContent = await fetchInlineEdit({
        sectionName: activeSection.sectionName,
        currentContent,
        userPrompt: enrichedPrompt,
        jobDescription,
      });

      const nextOptimizedData = applyInlineEditResult(
        optimizedData,
        selectedSection,
        updatedContent
      );

      onApplyUpdate(nextOptimizedData);
      setUserPrompt("");
    } catch (err) {
      setError(err.message || "Failed to apply AI edit.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
        AI Resume Editor
      </Typography>

      {jobTitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Optimised for: <strong>{jobTitle}</strong>
        </Typography>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Click edit on the preview or pick a field below, then apply AI changes. Save when done.
      </Typography>

      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel id="advanced-section-select">Select Field</InputLabel>
        <Select
          labelId="advanced-section-select"
          label="Select Field"
          value={selectedSection}
          onChange={(e) => {
            setSelectedSection(e.target.value);
            setError("");
          }}
        >
          {sectionOptions.map((option) => (
            <MenuItem key={option.key} value={option.key}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        inputRef={promptRef}
        fullWidth
        multiline
        minRows={4}
        maxRows={8}
        label="Your Prompt"
        placeholder='e.g. "Make the summary more concise and add cloud experience"'
        value={userPrompt}
        onChange={(e) => {
          setUserPrompt(e.target.value);
          setError("");
        }}
        sx={{ mb: 2 }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        variant="contained"
        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AutoFixHighIcon />}
        onClick={handleApplyEdit}
        disabled={loading || saving || !userPrompt.trim()}
        sx={{ mb: 2 }}
      >
        {loading ? "AI Updating..." : "Apply AI Change"}
      </Button>

      {onSave && (
        <Button
          variant="contained"
          color="success"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={onSave}
          disabled={saving || loading || regenerating}
          sx={{ mb: 2 }}
        >
          {saving ? "Saving Resume..." : "Save Resume"}
        </Button>
      )}

      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: "flex", gap: 1, mt: "auto" }}>
        <Button variant="outlined" onClick={onBack} fullWidth disabled={saving}>
          Back
        </Button>
        <Button
          variant="outlined"
          onClick={onRegenerate}
          disabled={regenerating || saving}
          fullWidth
        >
          Regenerate All
        </Button>
      </Box>
    </Box>
  );
}
